import { useState } from 'react'
import { useNavigate, Navigate, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useAuth from '../hooks/useAuth'
import { ErrorAlert } from '../components/ui'

const ROLE_HOME = {
  admin: '/admin',
  facilitator: '/facilitator',
  client: '/client',
}

export default function Login() {
  const { t } = useTranslation('auth')
  const { user, role, status } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const { signIn } = useAuth()

  // Already logged in — redirect to where they came from, or role-specific dashboard
  const redirectTo = location.state?.from || ROLE_HOME[role] || '/'
  if (user && role) {
    return <Navigate to={redirectTo} replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await signIn(email, password)
      // After signIn, the auth state change listener in AuthContext updates user/role.
      // We need to read the role from the newly signed-in user to redirect.
      // The Navigate above will handle it on re-render, but for immediate redirect:
      // We'll let the component re-render with the new user state.
    } catch (err) {
      setError(err.message === 'Invalid login credentials'
        ? t('login.invalidCredentials')
        : err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5">
      <div className="w-full max-w-md animate-fadeIn">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl font-medium text-gray-800 mb-2">
            {t('login.title')}
          </h1>
          <p className="text-gray-500">{t('login.subtitle')}</p>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm">
          <ErrorAlert message={error} onDismiss={() => setError(null)} />

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('login.emailLabel')}</label>
              <input
                type="email"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('login.emailPlaceholder')}
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block font-semibold text-gray-800 mb-2">{t('login.passwordLabel')}</label>
              <input
                type="password"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base transition-all focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.passwordPlaceholder')}
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl font-semibold transition-all hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>

          <div className="text-center mt-5 space-y-3">
            <Link
              to="/forgot-password"
              className="text-primary-600 text-sm font-semibold hover:underline block"
            >
              {t('login.forgotPassword')}
            </Link>
            <p className="text-gray-500 text-sm">
              {t('login.registerPrompt')}{' '}
              <Link
                to="/facilitator/signup"
                className="text-primary-600 font-semibold hover:underline"
              >
                {t('login.signUpLink')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
